<template>
  <v-text-field
    ref="field"
    :value="display"
    v-bind="$attrs"
    :outlined="outlined"
    :dense="dense"
    :hide-details="hideDetails"
    :disabled="disabled"
    :readonly="readonly"
    :clearable="clearable"
    :color="color"
    :class="fieldClass"
    type="text"
    inputmode="decimal"
    v-on="listeners"
    @input="onInput"
    @focus="$event.target.select()"
  >
    <template #label v-if="label && !$slots.label && !$scopedSlots.label">
      <label class="p-label">{{ label }}</label>
    </template>
    <template v-for="(_, name) in $slots" :slot="name">
      <slot :name="name" />
    </template>
    <template v-for="(_, name) in scopedOnlySlots()" v-slot:[name]="slotData">
      <slot :name="name" v-bind="slotData" />
    </template>
  </v-text-field>
</template>

<script>
export default {
  name: "MoneyField",
  inheritAttrs: false,
  props: {
    value: {
      default: null,
    },
    modelValue: {
      default: undefined,
    },
    label: {
      type: String,
      default: "",
    },
    precision: {
      type: Number,
      default: 0,
    },
    thousands: {
      type: String,
      default: ",",
    },
    decimal: {
      type: String,
      default: ".",
    },
    allowNegative: {
      type: Boolean,
      default: false,
    },
    outlined: {
      type: Boolean,
      default: true,
    },
    dense: {
      type: Boolean,
      default: true,
    },
    hideDetails: {
      type: [Boolean, String],
      default: true,
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    color: {
      type: String,
      default: "first",
    },
    fieldClass: {
      type: String,
      default: "text-center",
    },
  },
  data() {
    return {
      display: this.toDisplay(this.parse(this.initialValue())),
    };
  },
  computed: {
    // رویداد input خودِ کامپوننت مدیریت می‌شود؛ بقیه‌ی رویدادها به v-text-field می‌رسند.
    listeners() {
      const listeners = { ...this.$listeners };
      delete listeners.input;
      return listeners;
    },
  },
  watch: {
    value(val) {
      this.syncFromParent(val);
    },
    modelValue(val) {
      this.syncFromParent(val);
    },
  },
  methods: {
    // اسلات‌هایی که پارامتر می‌گیرند (مثل message) و در $slots نیستند.
    scopedOnlySlots() {
      const scoped = {};
      Object.keys(this.$scopedSlots).forEach((name) => {
        if (!this.$slots[name]) scoped[name] = this.$scopedSlots[name];
      });
      return scoped;
    },
    initialValue() {
      return this.value != null ? this.value : this.modelValue;
    },
    // مقدارِ آمده از والد فقط وقتی روی نمایش می‌نشیند که واقعاً با عددِ فعلی
    // فرق داشته باشد؛ در غیر این صورت متنِ در حال تایپ کاربر بازنویسی می‌شد.
    syncFromParent(val) {
      const current = this.toNumber(this.parse(this.display));
      const incoming = val === "" || val === undefined ? null : val;
      if (current === incoming) return;
      this.display = this.toDisplay(this.parse(incoming));
    },
    normalizeDigits(raw) {
      return String(raw)
        .replace(/[۰-۹]/g, (d) => String.fromCharCode(d.charCodeAt(0) - 1728))
        .replace(/[٠-٩]/g, (d) => String.fromCharCode(d.charCodeAt(0) - 1584));
    },
    // ورودی خام (تایپ کاربر یا مقدار والد) را به اجزای عدد می‌شکند.
    parse(raw) {
      if (raw === null || raw === undefined || raw === "") {
        return { negative: false, intPart: "", decPart: null };
      }
      let text = this.normalizeDigits(raw);
      const negative = this.allowNegative && text.indexOf("-") !== -1;
      text = text.replace(new RegExp(`\\${this.decimal}`, "g"), ".");
      text = text.replace(/[^\d.]/g, "");
      const dotIndex = this.precision > 0 ? text.indexOf(".") : -1;
      let intPart = text;
      let decPart = null;
      if (dotIndex !== -1) {
        intPart = text.slice(0, dotIndex);
        decPart = text
          .slice(dotIndex + 1)
          .replace(/\./g, "")
          .slice(0, this.precision);
      }
      intPart = intPart.replace(/\./g, "").replace(/^0+(?=\d)/, "");
      return { negative, intPart, decPart };
    },
    toDisplay(parts) {
      if (parts.intPart === "" && parts.decPart === null) {
        return parts.negative ? "-" : "";
      }
      const grouped = parts.intPart.replace(
        /\B(?=(\d{3})+(?!\d))/g,
        this.thousands,
      );
      let text = (parts.negative ? "-" : "") + grouped;
      if (parts.decPart !== null) {
        text += this.decimal + parts.decPart;
      }
      return text;
    },
    // فیلد خالی یعنی null، نه 0؛ فقط وقتی رقمی وارد شده باشد عدد برگردانده می‌شود.
    toNumber(parts) {
      if (parts.intPart === "" && !parts.decPart) return null;
      const text =
        (parts.negative ? "-" : "") +
        (parts.intPart === "" ? "0" : parts.intPart) +
        (parts.decPart ? "." + parts.decPart : "");
      const num = Number(text);
      return isNaN(num) ? null : num;
    },
    // تعداد کاراکترهای معنادار (رقم، ممیز، علامت) تا محل کرسر، تا بعد از
    // قالب‌بندی دوباره کرسر سر جای خودش برگردد و تایپ وسط عدد به هم نریزد.
    countMeaningful(text, upTo) {
      let count = 0;
      for (let i = 0; i < upTo && i < text.length; i++) {
        if (text[i] !== this.thousands) count++;
      }
      return count;
    },
    caretFromCount(text, count) {
      let seen = 0;
      for (let i = 0; i < text.length; i++) {
        if (seen === count) return i;
        if (text[i] !== this.thousands) seen++;
      }
      return text.length;
    },
    inputElement() {
      return this.$refs.field ? this.$refs.field.$refs.input : null;
    },
    onInput(raw) {
      const element = this.inputElement();
      const rawText = raw === null || raw === undefined ? "" : String(raw);
      const meaningfulBeforeCaret = element
        ? this.countMeaningful(rawText, element.selectionStart)
        : null;
      const parts = this.parse(rawText);
      this.display = this.toDisplay(parts);
      const number = this.toNumber(parts);
      this.$emit("input", number);
      this.$emit("update:modelValue", number);
      this.$nextTick(() => {
        const input = this.inputElement();
        if (!input) return;
        if (input.value !== this.display) input.value = this.display;
        if (this.$refs.field) this.$refs.field.lazyValue = this.display;
        if (meaningfulBeforeCaret === null) return;
        const caret = this.caretFromCount(this.display, meaningfulBeforeCaret);
        input.setSelectionRange(caret, caret);
      });
    },
  },
};
</script>
